import React, { useRef, useEffect, useState } from 'react';
import { Box, CircularProgress, Button } from '@mui/material';
import RotatingBanner from './RotatingBanner';
import useConfig from '../hooks/useConfig';
import useScrollPosition from '../hooks/useScrollPosition';

// Screen-reader-only styling. The design has no visible page titles, but every
// page still needs exactly one <h1> naming its subject for assistive tech and
// search engines. Pages with a visible <h1> of their own omit `pageTitle`.
const visuallyHidden = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

const PageWrapper = ({
    configKey,
    pageTitle,
    children
}) => {
    const { config, loading, error } = useConfig(configKey);
    const { scrollPosition } = useScrollPosition();
    const bannerRef = useRef(null);
    const [bannerHeight, setBannerHeight] = useState(0);
    const [isButtonFixed, setIsButtonFixed] = useState(false);

    // Calculate when to fix the button to top
    useEffect(() => {
        if (bannerRef.current) {
            const bannerBottom = bannerRef.current.offsetTop + bannerRef.current.offsetHeight;
            setBannerHeight(bannerBottom);
            
            // Button should become fixed when it would naturally reach the top position
            // Account for the button's initial offset (-30px) and target position (16px)
            const targetTopPosition = 16; // Where we want it fixed at top
            const transitionPoint = bannerBottom + targetTopPosition;
            
            const shouldBeFixed = scrollPosition > transitionPoint;
            setIsButtonFixed(shouldBeFixed);
        }
    }, [scrollPosition, config]);

    if (loading) {
        return (
            <Box sx={{
                width: '100%',
                overflow: 'hidden',
                position: 'relative',
            }}>
                <RotatingBanner />
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 3,
                    minHeight: '200px'
                }}>
                    <CircularProgress size={40} />
                </Box>
            </Box>
        );
    }

    if (error) {
        return <Box sx={{ p: 3 }}>Error loading configuration: {error}</Box>;
    }

    if (!config) {
        return <Box sx={{ p: 3 }}>No configuration found</Box>;
    }

    // Extract banner configuration
    const bannerConfig = config.banner;
    let bannerImages = [];
    let bannerAlignments = [];
    let bannerCustomHeight = undefined;
    let bannerInterval = undefined;

    if (bannerConfig && bannerConfig.images) {
        // Construct full URLs and alignments from config
        bannerImages = bannerConfig.images.map(img => {
            // If URL is relative, add process.env.PUBLIC_URL
            if (img.url.startsWith('/')) {
                return `${process.env.PUBLIC_URL}${img.url}`;
            }
            // If URL is already absolute, use as-is
            return img.url;
        });
        bannerAlignments = bannerConfig.images.map(img => img.alignment);
        bannerCustomHeight = bannerConfig.customHeight;
        bannerInterval = bannerConfig.interval;
    }

    return (
        <Box sx={{
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
        }}>
            {pageTitle && (
                <Box component="h1" sx={visuallyHidden}>
                    {pageTitle}
                </Box>
            )}

            {/* Banner container with register button positioned relative to it */}
            <Box ref={bannerRef} sx={{ position: 'relative' }}>
                <RotatingBanner 
                    images={bannerImages}
                    customHeight={bannerCustomHeight}
                    alignments={bannerAlignments}
                    interval={bannerInterval}
                />
                
                {/* Big Top Button - positioned at bottom of banner or fixed at top */}
                {config.bigTopButton && (
                    <Box
                        sx={{
                            position: isButtonFixed ? 'fixed' : 'absolute',
                            top: isButtonFixed ? '16px' : 'auto',
                            bottom: isButtonFixed ? 'auto' : '-30px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1000,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            size="large"
                            href={config.bigTopButton.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                borderRadius: '50px', // Fully rounded sides
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                '&:hover': {
                                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                                    transform: 'translateY(-1px)',
                                    backgroundColor: 'secondary.dark', // Orange on hover
                                },
                                transition: 'all 0.2s ease-in-out',
                            }}
                        >
                            {config.bigTopButton.text}
                        </Button>
                    </Box>
                )}
            </Box>
            
            {typeof children === 'function' ? children(config) : children}
        </Box>
    );
};

export default PageWrapper; 